import React, { useEffect, useMemo, useRef, useState } from "react";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import {
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
} from "../../../components/Component";
import * as app_consts from "../../../constants/react_app_consts";
import { useSelector, useDispatch } from "react-redux";
import {
  fail_retrieve_delivery_notes_ticket_list,
  loading_retrieve_delivery_notes_ticket_list,
  reset_retrieve_delivery_notes_ticket_list,
  success_retrieve_delivery_notes_ticket_list,
} from "../../../reducers/dashboard_reducers/delivery_notes/retrieve_delivery_notes_ticket_list_Slice";
import {
  RETRIEVE_DELIVERY_NOTES_TICKETS_ACCESS_DENIED_MSG,
  RETRIEVE_DELIVERY_NOTES_TICKETS_ERR_MSG,
  RETRIEVE_DELIVERY_NOTES_TICKETS_SUCCESS_MSG,
} from "../../../constants/success_error_messages_consts";
import { RETRIEVE_DELIVERY_NOTE_TICKET_LIST_API, RETRIEVE_SPECIFIC_DELIVERY_NOTE_API } from "../../../api/api_data";
import { employee_dashboard_actions_helper } from "../../../actions/actions_helper";
import { messageToast } from "../../../utils/Utils";
import { findModuleAction, formatToSentenceCase } from "../../../utils/functionalUtils";
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import AuthLoader from "../../components/page_messaging/AuthLoader";
import AuthError from "../../components/page_messaging/AuthError";
import AuthNoData from "../../components/page_messaging/AuthNoData";
import moment from "moment";
import ModalAdd from "../components/modal/modal_add/ModalAdd";

import { useMinLoading } from "../../../Hooks/useMinLoading";
import usePersistentState from "../../../Hooks/usePersistentState";
import { makeEmployeeDashboardApiRequest } from "../../../api/api_helper";

const type = app_consts.DELIVERY_NOTES_WEIGH_BRIDGE_TICKET_LIST;

const initial_actions_data = {
  create_goods_received_weigh_bridge_ticket: false,
  create_delivery_note_weigh_bridge_ticket: false,
};

const PRODUCT_PREVIEW_LIMIT = 3;
const STATUS_COLORS = {
  success: "#1f9d55",
  warning: "#d69e2e",
  info: "#3182ce",
  danger: "#e53e3e",
  secondary: "#718096",
};

const firstNonEmptyProductList = (...candidates) => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  return [];
};

const getDeliveryNoteProducts = (item = {}) =>
  firstNonEmptyProductList(
    item?.product_list,
    item?.delivery_note?.product_list,
    item?.delivery_note_products,
    item?.products,
    item?.loading_order?.products
  );

const getDeliveryNoteProductNames = (item = {}) =>
  [
    ...new Set(
      getDeliveryNoteProducts(item)
        .map(
          (product) =>
            product?.product_name ||
            product?.product_label ||
            product?.product?.product_name ||
            product?.product ||
            product?.name ||
            ""
        )
        .filter(Boolean)
    ),
  ];

const getDeliveryNoteStatusMeta = (status = "") => {
  const normalizedStatus = String(status || "").trim().toLowerCase();

  if (
    normalizedStatus === String(app_consts.DN_PROCESSED || "").toLowerCase() ||
    normalizedStatus === String(app_consts.PROCESSING_COMPLETE || "").toLowerCase()
  ) {
    return { color: "success", label: "Processing Complete" };
  }

  if (normalizedStatus === String(app_consts.PENDING_DN_PROCESSING || "").toLowerCase()) {
    return { color: "warning", label: "Pending DN Processing" };
  }

  if (normalizedStatus === String(app_consts.UNDERGOING_PROCESSING || "").toLowerCase()) {
    return { color: "info", label: "Undergoing Processing" };
  }

  if (normalizedStatus.includes("cancel")) {
    return { color: "danger", label: "Cancelled" };
  }

  return { color: "secondary", label: status || "Unknown" };
};

const getDeliveryNoteStatusDetails = (item = {}) => {
  const rawStatus =
    item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status ||
    item?.weigh_bridge_ticket_status ||
    item?.weigh_bridge_ticket?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status ||
    item?.weigh_bridge_ticket?.weigh_bridge_ticket_status ||
    "";

  const statusId = Number(
    item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status_id ??
      item?.weigh_bridge_ticket?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status_id ??
      item?.weigh_bridge_ticket?.weigh_bridge_ticket_status_id
  );
  const normalizedStatus = String(rawStatus || "").trim().toLowerCase();

  if (
    statusId === app_consts.WEIGH_BRIDGE_TICKET_PROCESSING_COMPLETE ||
    statusId === app_consts.WEIGH_BRIDGE_TICKET_DELIVERED
  ) {
    return {
      color: "success",
      label:
        statusId === app_consts.WEIGH_BRIDGE_TICKET_DELIVERED ? "Delivered" : "Processing Complete",
      rawStatus,
    };
  }

  if (statusId === app_consts.WEIGH_BRIDGE_TICKET_IN_TRANSIT) {
    return { color: "info", label: "In Transit", rawStatus };
  }

  if (
    statusId === app_consts.WEIGH_BRIDGE_TICKET_PROCESSING_OUTBOUND ||
    statusId === app_consts.WEIGH_BRIDGE_TICKET_ONBOARDING_GOODS
  ) {
    return {
      color: "warning",
      label:
        statusId === app_consts.WEIGH_BRIDGE_TICKET_PROCESSING_OUTBOUND
          ? "Processing Outbound"
          : "Onboarding Goods",
      rawStatus,
    };
  }

  if (
    normalizedStatus.includes("processed") ||
    normalizedStatus.includes("complete") ||
    normalizedStatus.includes("delivered")
  ) {
    return {
      color: "success",
      label: normalizedStatus.includes("deliver") ? "Delivered" : "Processing Complete",
      rawStatus,
    };
  }

  if (normalizedStatus.includes("transit")) {
    return { color: "info", label: "In Transit", rawStatus };
  }

  if (
    normalizedStatus.includes("outbound") ||
    normalizedStatus.includes("onboarding") ||
    normalizedStatus === String(app_consts.PENDING_DN_PROCESSING || "").toLowerCase()
  ) {
    return {
      color: "warning",
      label: normalizedStatus.includes("outbound")
        ? "Processing Outbound"
        : normalizedStatus.includes("onboarding")
          ? "Onboarding Goods"
          : "Pending DN Processing",
      rawStatus,
    };
  }

  if (normalizedStatus.includes("cancel")) {
    return { color: "danger", label: "Cancelled", rawStatus };
  }

  const statusMeta = getDeliveryNoteStatusMeta(rawStatus);
  return { ...statusMeta, rawStatus };
};

const mergeDeliveryNoteRowWithDetails = (item = {}, details = null) => {
  if (!details) {
    return item;
  }

  return {
    ...item,
    ...details,
    delivery_note: {
      ...(item?.delivery_note || {}),
      ...(details?.delivery_note || {}),
    },
    consigner: {
      ...(item?.consigner || {}),
      ...(details?.consigner || {}),
    },
    loading_order: {
      ...(item?.loading_order || {}),
      ...(details?.loading_order || {}),
    },
    weigh_bridge_ticket: {
      ...(item?.weigh_bridge_ticket || {}),
      ...(details?.weigh_bridge_ticket || {}),
    },
    weigh_bridge_ticket_status:
      details?.weigh_bridge_ticket_status || item?.weigh_bridge_ticket_status,
    product_list: details?.product_list || item?.product_list,
  };
};

const DELIVERY_NOTE_STATUS_LEGEND = [
  { color: "warning", label: "Onboarding Goods" },
  { color: "warning", label: "Processing Outbound" },
  { color: "info", label: "In Transit" },
  { color: "success", label: "Delivered / Complete" },
  { color: "danger", label: "Cancelled" },
];

const DeliveryNotesList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page_data, setPageData] = useState([]);
  const [componentData, setComponentData] = useState(initial_actions_data);

  // rawLoading = true loading based on reducer status
  const [rawLoading, setRawLoading] = useState(true);

  const [error, setError] = useState("");
  const [account_actions, setAccountActions] = useState([]);
  const [sm, updateSm] = useState(false);
  const [searchTerm, setSearchTerm] = usePersistentState("delivery_notes_list:searchTerm", "");
  const [selectedFilter, setSelectedFilter] = usePersistentState("delivery_notes_list:selectedFilter", "All");
  const [currentPage, setCurrentPage] = usePersistentState("delivery_notes_list:currentPage", 1);
  const [itemPerPage] = useState(10);
  const [deliveryNoteDetailsByRef, setDeliveryNoteDetailsByRef] = usePersistentState("delivery_notes_list:detailsByRef", {});
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const [add_modal, setAddModal] = useState(false);
  const skipInitialPageResetRef = useRef(true);
  const deliveryNoteDetailsInFlightRef = useRef({});

  // UI loading + hasFetched
  const { loading, hasFetched } = useMinLoading(rawLoading, 300);

  const shouldIgnoreRowClick = (target) => {
    if (!target || typeof target.closest !== "function") return false;

    return Boolean(
      target.closest("a, button, input, select, textarea, label, .nk-tb-col-tools, .dropdown-toggle, .dropdown-menu")
    );
  };

  const handleRowNavigation = (event, deliveryNoteRefNumber) => {
    if (!deliveryNoteRefNumber || shouldIgnoreRowClick(event.target)) return;
    navigate(`/dashboard/delivery_notes/${deliveryNoteRefNumber}`);
  };

  // UseEffect that Runs When the Page Opens
  useEffect(() => {
    retrieve_delivery_notes_ticket_list_func();

    if (localStorage.getItem("log_cred")) {
      setAccountActions(JSON.parse(localStorage.getItem("log_cred"))?.user_actions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen to Changes In Account Actions
  useEffect(() => {
    if (account_actions) {
      let actions_data = { ...initial_actions_data };

      account_actions?.map((module) => {
        if (module.module_name === app_consts.WAREHOUSE_MANAGEMENT) {
          if (findModuleAction(app_consts.CREATE_DELIVERY_NOTE_WEIGH_BRIDGE_TICKET, module)) {
            actions_data.create_delivery_note_weigh_bridge_ticket = true;
          }
        }
      });

      setComponentData(actions_data);
    }
  }, [account_actions]);

  const canCreateDN =
    findModuleAction(app_consts.CREATE_DELIVERY_NOTE_WEIGH_BRIDGE_TICKET, componentData) !== null &&
    componentData?.create_delivery_note_weigh_bridge_ticket;

  // Retrieve Delivery Notes Tickets
  const retrieve_delivery_notes_ticket_list_func = () => {
    dispatch(
      employee_dashboard_actions_helper(
        loading_retrieve_delivery_notes_ticket_list,
        success_retrieve_delivery_notes_ticket_list,
        fail_retrieve_delivery_notes_ticket_list,
        RETRIEVE_DELIVERY_NOTES_TICKETS_ERR_MSG,
        RETRIEVE_DELIVERY_NOTES_TICKETS_SUCCESS_MSG,
        app_consts.GET,
        RETRIEVE_DELIVERY_NOTE_TICKET_LIST_API,
        null,
        RETRIEVE_DELIVERY_NOTES_TICKETS_ACCESS_DENIED_MSG
      )
    );
  };

  // Refresh List Function
  const refreshList = () => {
    setError("");
    setRawLoading(true);
    retrieve_delivery_notes_ticket_list_func();
    setPageData([]);
  };

  const retrieve_delivery_notes_ticket_list = useSelector(
    (state) => state.retrieve_delivery_notes_ticket_list.retrieve_delivery_notes_ticket_list
  );

  useEffect(() => {
    if (retrieve_delivery_notes_ticket_list.status !== app_consts.IDLE) {
      if (retrieve_delivery_notes_ticket_list.status === app_consts.LOADING) {
        setError("");
        setRawLoading(true);
      } else {
        if (retrieve_delivery_notes_ticket_list.status === app_consts.FAIL) {
          setRawLoading(false);
          setError(retrieve_delivery_notes_ticket_list.message);
          messageToast(RETRIEVE_DELIVERY_NOTES_TICKETS_ERR_MSG, app_consts.FAIL);
        } else if (retrieve_delivery_notes_ticket_list.status === app_consts.SUCCESS) {
          setPageData(retrieve_delivery_notes_ticket_list.data);
          setRawLoading(false);
        }

        dispatch(reset_retrieve_delivery_notes_ticket_list());
      }
    }
  }, [retrieve_delivery_notes_ticket_list, dispatch]);

  const filterDNs = (value) => {
    setSelectedFilter(value);
    setCurrentPage(1);
  };

  const open_add_modal = () => setAddModal(true);
  const close_add_modal = () => setAddModal(false);

  const successAddFunction = () => {
    close_add_modal();
  };

  const current_page_data = useMemo(() => {
    let filteredData = page_data;

    // filter first
    if (selectedFilter === "Physical Delivery Notes") {
      filteredData = filteredData?.filter(
        (dn) => dn?.delivery_note?.dn_origin?.toLowerCase() === app_consts.WAREHUOSE_DELIVERY?.toLowerCase()
      );
    } else if (selectedFilter === "Direct Delivery Notes") {
      filteredData = filteredData?.filter(
        (dn) => dn?.delivery_note?.dn_origin?.toLowerCase() === app_consts.GRN_SCHEDULE?.toLowerCase()
      );
    }

    // then search
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();

      filteredData = filteredData?.filter((item) => {
        return (
          item?.delivery_note?.delivery_note_ref_number?.toLowerCase().includes(lowerSearch) ||
          item?.delivery_note?.dn_origin?.toLowerCase().includes(lowerSearch) ||
          item?.consigner?.name?.toLowerCase().includes(lowerSearch) ||
          item?.consigner?.unique_identification_number?.toLowerCase().includes(lowerSearch) ||
          getDeliveryNoteProductNames(item).join(" ").toLowerCase().includes(lowerSearch) ||
          item?.weigh_bridge_ticket?.truck_licence_plate?.toLowerCase().includes(lowerSearch) ||
          item?.weigh_bridge_ticket?.trailer_one?.toLowerCase().includes(lowerSearch) ||
          item?.weigh_bridge_ticket?.trailer_two?.toLowerCase().includes(lowerSearch) ||
          item?.sum_dn_products?.toString().toLowerCase().includes(lowerSearch) ||
          (item?.delivery_note?.created_date &&
            moment(item?.delivery_note?.created_date)
              .format("YYYY-MM-DD h:mm:ss")
              .toLowerCase()
              .includes(lowerSearch)) ||
          item?.weigh_bridge_ticket?.weigh_bridge_ticket_status?.toLowerCase().includes(lowerSearch)
        );
      });
    }

    return filteredData || [];
  }, [searchTerm, selectedFilter, page_data]);

  useEffect(() => {
    if (skipInitialPageResetRef.current) {
      skipInitialPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [searchTerm, selectedFilter, setCurrentPage]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(current_page_data.length / itemPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [currentPage, current_page_data.length, itemPerPage, setCurrentPage]);

  // Get current list, pagination
  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = current_page_data?.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    const refsToFetch = currentItems
      ?.map((item) => item?.delivery_note?.delivery_note_ref_number)
      .filter(
        (refNo) =>
          refNo &&
          !deliveryNoteDetailsByRef[refNo] &&
          !deliveryNoteDetailsInFlightRef.current[refNo]
      );

    if (!Array.isArray(refsToFetch) || refsToFetch.length === 0) {
      return;
    }

    let shouldUpdateState = true;

    const fetchDeliveryNoteDetails = async () => {
      const results = await Promise.allSettled(
        refsToFetch.map(async (deliveryNoteRefNumber) => {
          deliveryNoteDetailsInFlightRef.current[deliveryNoteRefNumber] = true;

          try {
            const response = await makeEmployeeDashboardApiRequest(
              app_consts.POST,
              RETRIEVE_SPECIFIC_DELIVERY_NOTE_API,
              { delivery_note_ref_number: deliveryNoteRefNumber }
            );

            return {
              deliveryNoteRefNumber,
              details: response?.data?.data || null,
            };
          } finally {
            delete deliveryNoteDetailsInFlightRef.current[deliveryNoteRefNumber];
          }
        })
      );

      if (!shouldUpdateState) {
        return;
      }

      setDeliveryNoteDetailsByRef((previousState) => {
        const nextState = { ...previousState };

        results.forEach((result) => {
          if (result.status !== "fulfilled") {
            return;
          }

          if (result.value?.details) {
            nextState[result.value.deliveryNoteRefNumber] = result.value.details;
          }
        });

        return nextState;
      });
    };

    fetchDeliveryNoteDetails();

    return () => {
      shouldUpdateState = false;
    };
  }, [currentItems, deliveryNoteDetailsByRef]);

  // only allow empty state after first fetch finishes (prevents flicker)
  const showNoData = hasFetched && !loading && !error && (!page_data || page_data.length === 0);
  const shouldShowInitialLoader = !hasFetched && !error && (!page_data || page_data.length === 0);

  return (
    <>
      <Head title="Delivery Notes" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Delivery Notes</BlockTitle>
              <BlockDes className="text-soft">
                <p>{!(loading || error) && ` Delivery Notes ${current_page_data?.length}`}</p>
              </BlockDes>

              {/* Refresh */}
              {!loading && (
                <div className="mt-2">
                  <Link
                    onClick={(e) => {
                      e.preventDefault();
                      refreshList();
                    }}
                  >
                    <Button color="light" outline className="bg-white d-none d-sm-inline-flex">
                      <span>Refresh List</span>
                    </Button>
                  </Link>
                </div>
              )}
            </BlockHeadContent>

            {!(loading || error) && (
              <BlockHeadContent>
                <div className="toggle-wrap nk-block-tools-toggle">
                  <a
                    href="#more"
                    className="btn btn-icon btn-trigger toggle-expand me-n1"
                    onClick={(ev) => {
                      ev.preventDefault();
                      updateSm(!sm);
                    }}
                  >
                    <Icon name="more-v"></Icon>
                  </a>

                  <div className="toggle-expand-content" style={{ display: sm ? "block" : "none" }}>
                    <ul className="nk-block-tools g-3">
                      {canCreateDN && (
                        <li className="nk-block-tools-opt">
                          <UncontrolledDropdown>
                            <DropdownToggle color="primary" className="btn d-inline-flex align-items-center">
                              <Icon name="plus"></Icon>
                              <span className="ms-2">Delivery Note Actions</span>
                              <Icon name="chevron-down" className="ms-2" />
                            </DropdownToggle>
                            <DropdownMenu end className="mt-1">
                              <DropdownItem onClick={open_add_modal}>Add Delivery Notes</DropdownItem>
                              <DropdownItem
                                tag={Link}
                                to="/dashboard/delivery_notes/create_physical_delivery_note_ticket"
                              >
                                Weighbridge Delivery Notes
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </BlockHeadContent>
            )}
          </BlockBetween>
        </BlockHead>

        {(loading || shouldShowInitialLoader) ? (
          <AuthLoader loading_message="Loading Delivery Notes..." />
        ) : error ? (
          <AuthError error_message={error} />
        ) : page_data && page_data?.length > 0 ? (
          <>
            <ul className="nk-block-tools g-3 d-flex justify-end">
              <li>
                <div className="">
                  <div className="form-icon form-icon-right">
                    <Icon name="search"></Icon>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    id="default-04"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search for Delivery Notes"
                  />
                </div>
              </li>

              <li className="preview-item">
                <UncontrolledDropdown>
                  <div className="btn-group">
                    <DropdownToggle color="light" outline className="dropdown-toggle-split btn-white">
                      <Icon name="chevron-down"></Icon>
                      <span>{selectedFilter}</span>
                    </DropdownToggle>
                  </div>

                  <DropdownMenu className="mt-3">
                    <ul className="link-list-opt">
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#"
                          onClick={(ev) => {
                            ev.preventDefault();
                            filterDNs("All");
                          }}
                        >
                          <span>All</span>
                        </DropdownItem>
                      </li>

                      <li>
                        <DropdownItem
                          tag="a"
                          href="#"
                          onClick={(ev) => {
                            ev.preventDefault();
                            filterDNs("Physical Delivery Notes");
                          }}
                        >
                          <span>Physical Delivery Notes</span>
                        </DropdownItem>
                      </li>

                      <li>
                        <DropdownItem
                          tag="a"
                          href="#"
                          onClick={(ev) => {
                            ev.preventDefault();
                            filterDNs("Direct Delivery Notes");
                          }}
                        >
                          <span>Direct Delivery Notes</span>
                        </DropdownItem>
                      </li>
                    </ul>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </li>
            </ul>

            <div className="d-flex flex-wrap align-items-center gap-2 mb-3 px-1">
              <span className="text-soft small">Status legend:</span>
              {DELIVERY_NOTE_STATUS_LEGEND.map((statusItem) => (
                <div key={statusItem.label} className="d-inline-flex align-items-center gap-1 me-2">
                  <span
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "999px",
                      display: "inline-block",
                      backgroundColor: STATUS_COLORS[statusItem.color] || STATUS_COLORS.secondary,
                    }}
                  />
                  <span className="small text-soft">{statusItem.label}</span>
                </div>
              ))}
            </div>

            <div className="nk-tb-list is-separate is-medium mt-1 mb-3">
              <div className="card-inner p-0">
                <DataTableBody>
                  <DataTableHead>
                    <DataTableRow size="sm">
                      <span>Delivery Note Ref No.</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Client</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Products</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Horse</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Trailer</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Total Tonnage (MT)</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Loading Order Ref.</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span>Date Created</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span>Status</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span>Action</span>
                    </DataTableRow>
                  </DataTableHead>

                  {currentItems?.length > 0
                    ? currentItems.map((item) => {
                        const deliveryNoteRefNumber = item?.delivery_note?.delivery_note_ref_number;
                        const hydratedItem = mergeDeliveryNoteRowWithDetails(
                          item,
                          deliveryNoteDetailsByRef[deliveryNoteRefNumber]
                        );
                        const statusMeta = getDeliveryNoteStatusDetails(hydratedItem);
                        const productNames = getDeliveryNoteProductNames(hydratedItem);

                        return (
                        <DataTableItem
                          key={deliveryNoteRefNumber}
                          className="cursor-pointer"
                          role="link"
                          tabIndex={0}
                          onClick={(event) => handleRowNavigation(event, deliveryNoteRefNumber)}
                          onKeyDown={(event) => {
                            if (event.key !== "Enter" && event.key !== " ") return;
                            event.preventDefault();
                            handleRowNavigation(event, deliveryNoteRefNumber);
                          }}
                        >
                          <DataTableRow size="sm">
                            <Link to={`/dashboard/delivery_notes/${deliveryNoteRefNumber}`}>
                              <span className="tb-product">
                                <span className="title">{deliveryNoteRefNumber || "--"}</span>
                              </span>
                              <div>
                                <span className="fw-medium">Origin: </span>
                                <span className="tb-sub">
                                  {hydratedItem?.delivery_note?.dn_origin
                                    ? formatToSentenceCase(hydratedItem?.delivery_note?.dn_origin)
                                    : "--"}
                                </span>
                              </div>
                            </Link>
                          </DataTableRow>

                          <DataTableRow>
                            <span className="tb-sub">
                              {item?.consigner?.name
                                ? item?.consigner?.name.length > 20
                                  ? `${item?.consigner?.name.substring(0, 20)}...`
                                  : item?.consigner?.name
                                : "---"}
                            </span>
                            <div>
                              <span className="fw-medium">ID: </span>
                              <span className="tb-sub">{item?.consigner?.unique_identification_number || "--"}</span>
                            </div>
                          </DataTableRow>

                          <DataTableRow>
                            <div>
                              {productNames.length > 0 ? (
                                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                                  {productNames.slice(0, PRODUCT_PREVIEW_LIMIT).map((productName) => (
                                    <li key={productName} className="tb-sub">{productName}</li>
                                  ))}
                                  {productNames.length > PRODUCT_PREVIEW_LIMIT && (
                                    <li className="tb-sub">+{productNames.length - PRODUCT_PREVIEW_LIMIT} more</li>
                                  )}
                                </ul>
                              ) : (
                                <span className="tb-sub">--</span>
                              )}
                            </div>
                          </DataTableRow>

                          <DataTableRow>
                            <span className="tb-sub">{hydratedItem?.weigh_bridge_ticket?.truck_licence_plate || "--"}</span>
                          </DataTableRow>

                          <DataTableRow>
                            <div>
                              <span className="fw-medium">Trailer 1: </span>
                              <span className="tb-sub">{hydratedItem?.weigh_bridge_ticket?.trailer_one || "--"}</span>
                            </div>
                            <div>
                              <span className="fw-medium">Trailer 2: </span>
                              <span className="tb-sub">{hydratedItem?.weigh_bridge_ticket?.trailer_two || "--"}</span>
                            </div>
                          </DataTableRow>

                          <DataTableRow>
                            <span className="tb-sub">{item?.sum_dn_products || "--"}</span>
                          </DataTableRow>

                          <DataTableRow>
                            <span className="tb-sub">{hydratedItem?.loading_order?.loading_order_ref_no || "--"}</span>
                          </DataTableRow>

                          <DataTableRow>
                            <span className="tb-sub">
                              {hydratedItem?.delivery_note?.created_date
                                ? moment(hydratedItem?.delivery_note?.created_date).format("YYYY-MM-DD h:mm:ss")
                                : "--"}
                            </span>
                          </DataTableRow>

                          <DataTableRow size="md">
                            <span className="tb-odr-status">
                              <span
                                title={statusMeta.label}
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  borderRadius: "999px",
                                  display: "inline-block",
                                  backgroundColor: STATUS_COLORS[statusMeta.color] || STATUS_COLORS.secondary,
                                }}
                              />
                            </span>
                          </DataTableRow>

                          <DataTableRow className="nk-tb-col-tools">
                            <div className="tb-odr-btns d-none d-sm-inline">
                              <Link to={`/dashboard/delivery_notes/${deliveryNoteRefNumber}`}>
                                <Button color="primary" size="sm" className="btn btn-dim">
                                  View
                                </Button>
                              </Link>
                            </div>
                            <Button className="btn-pd-auto d-sm-none">
                              <Icon name="chevron-right"></Icon>
                            </Button>
                          </DataTableRow>
                        </DataTableItem>
                      );
                    })
                    : null}
                </DataTableBody>

                {currentItems?.length > 0 && (
                  <div className="mt-3">
                    <PaginationComponent
                      itemPerPage={itemPerPage}
                      totalItems={current_page_data?.length}
                      paginate={paginate}
                      currentPage={currentPage}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        ) : showNoData ? (
          <AuthNoData no_data_message={"No Delivery Notes Data Found."} />
        ) : null}

        <ModalAdd
          openModal={add_modal}
          setOpenModal={setAddModal}
          modalTitle="Add Delivery Note"
          modalDescription="Please Complete this form to Add a Delivery Note"
          type={type}
          succFunc={() => successAddFunction()}
          size="xl"
        />
      </Content>
    </>
  );
};

export default DeliveryNotesList;
